/**
 * Implement a class that represents a tool.
 * - name - the name of the tool
 * - description - the description of the tool
 * - parameters - the parameters of the tool
 * - execute(): Promise<any> - the function that executes the tool
 * - setFollowUp() - add a follow-up message to the tool call
 * - toMessages(): Message[] - convert the tool to a list of messages include tool response messages and any follow-up messages
 */

import { nanoid } from 'nanoid';
import type { z } from "zod";
import { EventSource, type EventSourceInterface } from "./event-source";
import { Message, type ToolCall } from "./message";
import { traceable } from "./tracing";


export interface ToolParameter {
  name: string;
  type: "string" | "number" | "boolean" | "object" | "array";
  description: string;
  required?: boolean;
}

export interface ToolCallResult {
  success: boolean;
  result: any;
  toolCall: ToolCall;
  error?: string;
}

export interface ToolInterface extends EventSourceInterface {
  name: string;
  description: string;
  parameters: z.ZodTypeAny;
  executeTool: (args: any, toolCall: ToolCall) => Promise<any>;
}

export function makeToolFromInterface(t: ToolInterface): Tool {
  if (t instanceof Tool) {
    return t;
  }

  return new Tool({
    name: t.name,
    description: t.description,
    parameters: t.parameters,
    executeFn: async (args: Record<string, any>, toolCall: ToolCall) =>
      await t.executeTool(args, toolCall),
  });
}

interface ToolOptions {
  name: string;
  description: string;
  parameters: z.ZodTypeAny;
  followUpMessage?: string;
  executeFn: (args: Record<string, any>, toolCall: ToolCall) => Promise<any>;
  id?: string;
  trace?: boolean;
  traceMetadata?: Record<string, unknown>;
}

export class Tool extends EventSource implements ToolInterface {
  public readonly id: string;
  public followUpMessage?: string;
  public name: string;
  public description: string;
  public parameters: z.ZodTypeAny;
  private readonly executeFn: (args: Record<string, any>, toolCall: ToolCall) => Promise<any>;
  private readonly trace: boolean;
  private readonly traceMetadata?: Record<string, unknown>;

  constructor(options: ToolOptions) {
    super();
    this.id = options.id ?? `tool_${nanoid(15)}`;
    this.name = options.name;
    this.description = options.description;
    this.parameters = options.parameters;
    this.followUpMessage = options.followUpMessage;
    this.executeFn = options.executeFn;
    this.trace = options.trace ?? false;
    this.traceMetadata = options.traceMetadata;
  }

  /**
   * Get the name of the tool
   */
  public getName(): string {
    return this.name;
  }

  /**
   * Get the description of the tool
   */
  public getDescription(): string {
    return this.description;
  }

  /**
   * Get the parameters of the tool
   */
  public getParameters(): z.ZodTypeAny {
    return this.parameters;
  }

  /**
   * Execute the tool with the given arguments
   */
  public async executeTool(args: any, toolCall: ToolCall): Promise<ToolCallResult> {
    this.emit("tool.execution.started", {
      tool: this,
      name: this.name,
      args,
    });

    try {
      this.parameters.parse(args);

      // If tracing is enabled, wrap the execution function with traceable
      const result = await this.executeWithTracing(args, toolCall);

      this.emit("tool.execution.completed", {
        tool: this,
        name: this.name,
        args,
        result,
      });

      return {
        success: true,
        result,
        toolCall,
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";

      this.emit("tool.execution.failed", {
        tool: this,
        name: this.name,
        args,
        toolCall,
        error: errorMessage,
      });

      return {
        success: false,
        result: null,
        toolCall,
        error: errorMessage,
      };
    }
  }

  /**
   * Execute the tool with tracing if enabled
   * @param args Tool arguments
   * @param toolCall The tool call object
   * @returns Result of the tool execution
   */
  private async executeWithTracing(args: any, toolCall: ToolCall): Promise<any> {
    if (!this.trace) {
      return this.executeFn(args, toolCall);
    }

    // Create a formatted input object for better tracing display
    const formattedInput = this.formatToolInput(args);

    // Execute with tracing
    return traceable(async () => {
      const result = await this.executeFn(args, toolCall);
      return result;
    }, {
      name: this.name,
      run_type: "tool",
      input: formattedInput,
      // Include tool call ID and other metadata
      tool_call_id: toolCall.toolUseId,
      tool_description: this.description,
      ...this.traceMetadata
    })();
  }

  /**
   * Format tool inputs for better tracing display
   * @param args Raw tool arguments
   * @returns Formatted input object
   */
  private formatToolInput(args: Record<string, any>): Record<string, any> {
    // Basic input object with the raw parameters
    const input: Record<string, any> = {
      parameters: args
    };

    // Try to create a human-readable description based on the tool type
    if (this.name.toLowerCase().includes('calculator') && args.operation) {
      // For calculator tools
      input.operation_type = args.operation;
      
      // Create a readable expression
      if (typeof args.a !== 'undefined' && typeof args.b !== 'undefined') {
        input.expression = `${args.a} ${args.operation} ${args.b}`;
      }
    } else if (this.name.toLowerCase().includes('search') && args.query) {
      // For search tools
      input.search_query = args.query;
    } else if (this.name.toLowerCase().includes('weather') && args.location) {
      // For weather tools
      input.location = args.location;
    }

    // Try to add a summary field for any tool
    try {
      let summary = `${this.name}(`;
      const paramStrings = Object.entries(args).map(([key, value]) => {
        if (typeof value === 'string') {
          return `${key}: "${value}"`;
        }
        return `${key}: ${value}`;
      });
      summary += paramStrings.join(', ');
      summary += ')';
      input.summary = summary;
    } catch (e) {
      // Ignore errors in summary generation
    }

    return input;
  }

  /**
   * Set a follow-up message to be included after the tool response
   */
  public setFollowUp(message: string): void {
    this.followUpMessage = message;
    this.emit("tool.followup.set", {
      tool: this,
      message,
    });
  }

  /**
   * Convert the tool to a list of messages including tool response messages
   * and any follow-up messages
   */
  public toMessages(result: ToolCallResult): Message[] {
    const messages: Message[] = [
      new Message({
        role: "tool",
        content: this.followUpMessage || undefined,
        toolResults: [
          {
            name: this.name,
            result: result.result,
            toolUseId: result.toolCall.toolUseId,
            error: result.error,
          },
        ],
      }),
    ];

    this.emit("tool.messages.generated", {
      tool: this,
      messages,
    });

    return messages;
  }
}
